import { useEffect, useRef, useState, type RefObject } from "react";
import { isOutroBandRevealed } from "@/lib/chapterOutroReveal";

/** How the chapter-outro stack was entered (`unveil=1` → content, else Abschluss). */
const OUTRO_ENTRY_MODE = {
  Content: "content",
  Outro: "outro",
} as const;

type OutroEntryMode = (typeof OUTRO_ENTRY_MODE)[keyof typeof OUTRO_ENTRY_MODE];

interface ChapterOutroReveal {
  rootRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  outroRef: RefObject<HTMLDivElement | null>;
  outroHeadingRef: RefObject<HTMLHeadingElement | null>;
  /** True when Abschluss is the active band (content is locked context). */
  outroActive: boolean;
}

class NoopResizeObserver {
  observe() {}
  unobserve() {}
  disconnect() {}
}

const createResizeObserver = (callback: ResizeObserverCallback) => {
  if (typeof ResizeObserver === "undefined") {
    return new NoopResizeObserver();
  }
  return new ResizeObserver(callback);
};

const markOutroAsSeen = (
  seenRef: RefObject<boolean>,
  onOutroSeenRef: RefObject<(() => void) | undefined>
) => {
  if (seenRef.current) return;
  seenRef.current = true;
  onOutroSeenRef.current?.();
};

const focusOutroHeading = (
  headingRef: RefObject<HTMLHeadingElement | null>
) => {
  const heading = headingRef.current;
  if (!heading) return;
  heading.focus({ preventScroll: true });
};

const getScrollRoot = (root: HTMLElement | null) =>
  (root?.closest(".overflow-y-auto") as HTMLElement | null) ?? null;

const pinOutroInView = (
  root: HTMLElement | null,
  outro: HTMLElement | null
) => {
  const scrollRoot = getScrollRoot(root);
  if (!scrollRoot || !outro) return false;

  const scrollRootRect = scrollRoot.getBoundingClientRect();
  const outroRect = outro.getBoundingClientRect();
  scrollRoot.scrollTop += outroRect.top - scrollRootRect.top;
  return true;
};

/**
 * Syncs chapter-outro band focus with scroll — used by ChapterOutroScroll
 * for content→Abschluss unveil and deep-link landing on Abschluss.
 */
const useChapterOutroReveal = (
  initialMode: OutroEntryMode,
  onOutroSeen?: () => void
): ChapterOutroReveal => {
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const outroRef = useRef<HTMLDivElement>(null);
  const outroHeadingRef = useRef<HTMLHeadingElement>(null);
  const seenRef = useRef(initialMode === OUTRO_ENTRY_MODE.Outro);
  const onOutroSeenRef = useRef(onOutroSeen);
  const allowDeactivateRef = useRef(initialMode === OUTRO_ENTRY_MODE.Content);
  const [outroActive, setOutroActive] = useState(
    initialMode === OUTRO_ENTRY_MODE.Outro
  );
  const outroActiveRef = useRef(outroActive);

  useEffect(() => {
    onOutroSeenRef.current = onOutroSeen;
  }, [onOutroSeen]);

  useEffect(() => {
    if (initialMode !== OUTRO_ENTRY_MODE.Content) return;

    allowDeactivateRef.current = true;

    const pinContent = () => {
      const scrollRoot = getScrollRoot(rootRef.current);
      if (scrollRoot) {
        scrollRoot.scrollTop = 0;
      }
      outroActiveRef.current = false;
      setOutroActive(false);
    };

    pinContent();
    const frame = window.requestAnimationFrame(pinContent);
    return () => window.cancelAnimationFrame(frame);
  }, [initialMode]);

  useEffect(() => {
    if (initialMode !== OUTRO_ENTRY_MODE.Outro) return;

    markOutroAsSeen(seenRef, onOutroSeenRef);
    allowDeactivateRef.current = false;

    const pin = () => {
      pinOutroInView(rootRef.current, outroRef.current);
      focusOutroHeading(outroHeadingRef);
    };

    pin();
    const frame = window.requestAnimationFrame(pin);

    const content = contentRef.current;
    const resizeObserver =
      content != null
        ? createResizeObserver(() => {
            if (!allowDeactivateRef.current) {
              pin();
            }
          })
        : null;
    if (content && resizeObserver) resizeObserver.observe(content);

    const scrollRoot = getScrollRoot(rootRef.current);
    const unlockDeactivate = () => {
      allowDeactivateRef.current = true;
    };
    scrollRoot?.addEventListener("wheel", unlockDeactivate, { passive: true });
    scrollRoot?.addEventListener("touchstart", unlockDeactivate, {
      passive: true,
    });

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      scrollRoot?.removeEventListener("wheel", unlockDeactivate);
      scrollRoot?.removeEventListener("touchstart", unlockDeactivate);
    };
  }, [initialMode]);

  useEffect(() => {
    const scrollRoot = getScrollRoot(rootRef.current);
    const outro = outroRef.current;
    if (!scrollRoot || !outro) return;

    const syncReveal = () => {
      if (isOutroBandRevealed(outro, scrollRoot, outroActiveRef.current)) {
        outroActiveRef.current = true;
        setOutroActive(true);
        markOutroAsSeen(seenRef, onOutroSeenRef);
        return;
      }
      if (!allowDeactivateRef.current) return;
      outroActiveRef.current = false;
      setOutroActive(false);
    };

    scrollRoot.addEventListener("scroll", syncReveal, { passive: true });
    window.addEventListener("resize", syncReveal);
    const resizeObserver = createResizeObserver(syncReveal);
    resizeObserver.observe(scrollRoot);
    resizeObserver.observe(outro);
    syncReveal();

    return () => {
      scrollRoot.removeEventListener("scroll", syncReveal);
      window.removeEventListener("resize", syncReveal);
      resizeObserver.disconnect();
    };
  }, []);

  return {
    rootRef,
    contentRef,
    outroRef,
    outroHeadingRef,
    outroActive,
  };
};

export { useChapterOutroReveal, OUTRO_ENTRY_MODE };
export type { OutroEntryMode };
